import { type Icon } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import type LeaveForm from 'modules/timeoff/lib/LeaveForm'

const useDescription = (
  canApproveLeaves: boolean,
  timeoffManagerFullName: string | null,
  form: LeaveForm,
  isMedical: boolean,
  hasManualApprovalGroup: boolean
): string => {
  if (isMedical) {
    return i18n.t('time_off.medical_leave.confirm_header.description')
  }

  if (canApproveLeaves) {
    return i18n.t('time_off.employee.add_new.subtitle.admin')
  }

  // Only show auto-approval message if there's no supervisor AND no manual approval group
  if (!timeoffManagerFullName && !hasManualApprovalGroup) {
    return i18n.t('time_off.employee.add_new.subtitle.no_supervisor')
  }

  // If no supervisor but has approval group, use specific message
  if (!timeoffManagerFullName && hasManualApprovalGroup) {
    return i18n.t('time_off.employee.add_new.subtitle.basic_with_approval_group')
  }

  if (form.isSickType()) {
    return i18n.t('time_off.employee.add_new.subtitle.sick', {
      timeoffManager: timeoffManagerFullName,
    })
  }

  return i18n.t('time_off.employee.add_new.subtitle.basic', {
    timeoffManager: timeoffManagerFullName,
  })
}

const useTitle = (canApproveLeaves: boolean, isMedical: boolean): string => {
  if (isMedical) {
    return i18n.t('time_off.medical_leave.confirm_header.title')
  }

  if (canApproveLeaves) {
    return i18n.t('time_off.employee.add_new.title.admin')
  }

  return i18n.t('time_off.employee.add_new.title.basic')
}

interface Props {
  canApproveLeaves: boolean
  timeoffManagerFullName: string | null
  isMedical: boolean
  form: LeaveForm
  hasManualApprovalGroup: boolean
}
interface ReturnType {
  illustration: React.ComponentProps<typeof Icon.Large>['icon']
  title: string
  description: string
}

const useModalProps = ({
  isMedical,
  canApproveLeaves,
  timeoffManagerFullName,
  form,
  hasManualApprovalGroup,
}: Props): ReturnType => {
  const illustration = isMedical ? 'user-tie' : 'beach'
  const title = useTitle(canApproveLeaves, isMedical)
  const description = useDescription(
    canApproveLeaves,
    timeoffManagerFullName,
    form,
    isMedical,
    hasManualApprovalGroup
  )

  return {
    illustration,
    title,
    description,
  }
}

export default useModalProps
